import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useApolloClient } from '@apollo/client/react'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import ButtonDropdown from '@cloudscape-design/components/button-dropdown'
import Cards from '@cloudscape-design/components/cards'
import CollectionPreferences from '@cloudscape-design/components/collection-preferences'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Flashbar from '@cloudscape-design/components/flashbar'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import Modal from '@cloudscape-design/components/modal'
import Multiselect from '@cloudscape-design/components/multiselect'
import Pagination from '@cloudscape-design/components/pagination'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import Tabs from '@cloudscape-design/components/tabs'
import TextFilter from '@cloudscape-design/components/text-filter'
import Toggle from '@cloudscape-design/components/toggle'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import type { MultiselectProps } from '@cloudscape-design/components/multiselect'
import type { SelectProps } from '@cloudscape-design/components/select'
import type { ButtonDropdownProps } from '@cloudscape-design/components/button-dropdown'
import Page from '@/components/common/Page'
import { useClientsNav, useClient, useUpdateClient, useDeleteClient, useCreateClient } from '@/hooks/useClients'
import type { ClientNavItem } from '@/hooks/useClients'
import { updateClientSchema, createClientSchema } from '@/lib/schemas'
import type { UpdateClientFormData, CreateClientFormData } from '@/lib/schemas'
import { ALL_CONTENT_TYPES } from '@/lib/types'
import type { ClientConfig } from '@/lib/types'

// Intersection covers camelCase fields Apollo returns that aren't in the base type
type ClientWithCamel = ClientConfig & {
  websiteUrl?: string
  modelProvider?: string
  selectedContentTypes?: string[]
  autoRun?: boolean
  autoPublish?: boolean
  budgetLimitDailyUsd?: number
  brandSpecialization?: string
  seoKeywords?: string
  compliancePrompt?: string
  notificationEmail?: string
  notificationPhone?: string
}

const INDUSTRY_OPTIONS: SelectProps.Option[] = [
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'General', value: 'general' },
  { label: 'Legal', value: 'legal' },
  { label: 'Financial', value: 'financial' },
  { label: 'Food Service', value: 'food_service' },
  { label: 'Real Estate', value: 'real_estate' },
  { label: 'Education', value: 'education' },
  { label: 'Technology', value: 'technology' },
  { label: 'E-Commerce', value: 'ecommerce' },
  { label: 'Automotive', value: 'automotive' },
  { label: 'Construction', value: 'construction' },
  { label: 'Beauty & Wellness', value: 'beauty_wellness' },
  { label: 'Nonprofit', value: 'nonprofit' },
  { label: 'Professional Services', value: 'professional_services' },
  { label: 'Fitness', value: 'fitness' },
  { label: 'Travel & Hospitality', value: 'travel_hospitality' },
  { label: 'Home Services', value: 'home_services' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Agriculture', value: 'agriculture' },
  { label: 'Media & Entertainment', value: 'media_entertainment' },
]

const MODEL_PROVIDER_OPTIONS: SelectProps.Option[] = [
  { label: 'OpenAI', value: 'openai' },
  { label: 'Anthropic', value: 'anthropic' },
]

const CONTENT_TYPE_OPTIONS: MultiselectProps.Option[] = ALL_CONTENT_TYPES.map((ct) => ({
  label: ct.replace(/_/g, ' '),
  value: ct,
}))

const CATALOG_ITEMS = [
  {
    id: 'marketing-agent',
    name: 'Marketing Agent',
    description: 'Automates social media, blog, and ad content for your brand across multiple channels.',
  },
] as const

type CatalogItem = typeof CATALOG_ITEMS[number]

function mapClientToForm(c: ClientConfig): UpdateClientFormData {
  const d = c as ClientWithCamel
  return {
    brandName: d.brandName ?? d.brand_name ?? '',
    brandLocation: d.brandLocation ?? d.brand_location ?? '',
    brandSpecialization: d.brandSpecialization ?? d.brand_specialization ?? '',
    industry: d.industry ?? undefined,
    websiteUrl: d.websiteUrl ?? d.website_url ?? '',
    modelProvider: d.modelProvider ?? d.model_provider ?? undefined,
    agentName: d.agentName ?? d.agent_name ?? '',
    selectedContentTypes: d.selectedContentTypes ?? d.selected_content_types ?? [],
    autoRun: d.autoRun ?? d.auto_run ?? false,
    autoPublish: d.autoPublish ?? d.auto_publish ?? false,
    budgetLimitDailyUsd: d.budgetLimitDailyUsd ?? d.budget_limit_daily_usd ?? undefined,
    compliancePrompt: d.compliancePrompt ?? d.compliance_prompt ?? undefined,
    notificationEmail: d.notificationEmail ?? d.notification_email ?? undefined,
    notificationPhone: d.notificationPhone ?? d.notification_phone ?? undefined,
  }
}

// ─── EditAgentModal ───────────────────────────────────────────────────────────

interface EditAgentFormProps {
  agent: ClientConfig
  saving: boolean
  onSave: (data: UpdateClientFormData) => void
  onDismiss: () => void
}

function EditAgentForm({ agent, saving, onSave, onDismiss }: EditAgentFormProps) {
  const { control, handleSubmit, formState: { errors } } = useForm<UpdateClientFormData>({
    resolver: zodResolver(updateClientSchema),
    defaultValues: mapClientToForm(agent),
  })

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Edit Agent"
      size="large"
      data-testid="edit-agent-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={() => { void handleSubmit(onSave)() }}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Agent Name" errorText={errors.agentName?.message}>
              <Controller name="agentName" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Brand Name" errorText={errors.brandName?.message}>
              <Controller name="brandName" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Location" errorText={errors.brandLocation?.message}>
              <Controller name="brandLocation" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Specialization" errorText={errors.brandSpecialization?.message}>
              <Controller name="brandSpecialization" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Industry" errorText={errors.industry?.message}>
              <Controller name="industry" control={control} render={({ field }) => (
                <Select
                  options={INDUSTRY_OPTIONS}
                  selectedOption={INDUSTRY_OPTIONS.find((o) => o.value === field.value) ?? null}
                  onChange={({ detail }) => field.onChange(detail.selectedOption.value)}
                  placeholder="Select industry"
                />
              )} />
            </FormField>
            <FormField label="Website URL" errorText={errors.websiteUrl?.message}>
              <Controller name="websiteUrl" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Model Provider" errorText={errors.modelProvider?.message}>
              <Controller name="modelProvider" control={control} render={({ field }) => (
                <Select
                  options={MODEL_PROVIDER_OPTIONS}
                  selectedOption={MODEL_PROVIDER_OPTIONS.find((o) => o.value === field.value) ?? null}
                  onChange={({ detail }) => field.onChange(detail.selectedOption.value)}
                  placeholder="Select model provider"
                />
              )} />
            </FormField>
            <FormField label="Daily Budget (USD)" errorText={errors.budgetLimitDailyUsd?.message}>
              <Controller name="budgetLimitDailyUsd" control={control} render={({ field }) => (
                <Input
                  type="number"
                  value={field.value !== undefined ? String(field.value) : ''}
                  onChange={(e) => field.onChange(e.detail.value === '' ? undefined : Number(e.detail.value))}
                  onBlur={field.onBlur}
                />
              )} />
            </FormField>
          </ColumnLayout>
          <FormField label="Content Types" errorText={errors.selectedContentTypes?.message}>
            <Controller name="selectedContentTypes" control={control} render={({ field }) => (
              <Multiselect
                options={CONTENT_TYPE_OPTIONS}
                selectedOptions={(field.value ?? []).map((v) => ({ label: v.replace(/_/g, ' '), value: v }))}
                onChange={({ detail }) => field.onChange(detail.selectedOptions.map((o) => o.value as string))}
                placeholder="Select content types"
              />
            )} />
          </FormField>
          <Controller name="autoRun" control={control} render={({ field }) => (
            <Toggle checked={field.value ?? false} onChange={({ detail }) => field.onChange(detail.checked)}>
              Auto Run — automatically start campaigns
            </Toggle>
          )} />
          <Controller name="autoPublish" control={control} render={({ field }) => (
            <Toggle checked={field.value ?? false} onChange={({ detail }) => field.onChange(detail.checked)}>
              Auto Publish — automatically publish content
            </Toggle>
          )} />
        </SpaceBetween>
      </Form>
    </Modal>
  )
}

interface EditAgentModalProps {
  agentId: string
  onDismiss: () => void
  onSuccess: () => void
  onError: (msg: string) => void
}

function EditAgentModal({ agentId, onDismiss, onSuccess, onError }: EditAgentModalProps) {
  const { client: agent, loading: clientLoading } = useClient(agentId)
  const { updateClient, loading: saving } = useUpdateClient()
  const apolloClient = useApolloClient()

  const handleSave = async (data: UpdateClientFormData) => {
    // notificationEmail and notificationPhone are not in UpdateClientInput on the backend
    const { notificationEmail: _ne, notificationPhone: _np, ...updateInput } = data
    try {
      await updateClient(agentId, updateInput)
      await apolloClient.refetchQueries({ include: ['GetClientsNav'] })
      onSuccess()
    } catch {
      onError('Failed to update agent. Please try again.')
    }
  }

  if (clientLoading || !agent) {
    return (
      <Modal visible onDismiss={onDismiss} header="Edit Agent" size="large" data-testid="edit-agent-modal">
        <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>
      </Modal>
    )
  }

  return (
    <EditAgentForm
      agent={agent}
      saving={saving}
      onSave={(data) => { void handleSave(data) }}
      onDismiss={onDismiss}
    />
  )
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────

interface DeleteConfirmModalProps {
  agent: ClientNavItem | null
  onDismiss: () => void
  onSuccess: () => void
}

function DeleteConfirmModal({ agent, onDismiss, onSuccess }: DeleteConfirmModalProps) {
  const { deleteClient, loading } = useDeleteClient()

  const handleConfirm = async () => {
    if (!agent) return
    await deleteClient(agent.id)
    onSuccess()
  }

  const agentLabel = agent?.agentName ?? agent?.brandName ?? 'this agent'

  return (
    <Modal
      visible={agent !== null}
      onDismiss={onDismiss}
      header="Delete agent?"
      data-testid="delete-agent-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={loading} onClick={() => { void handleConfirm() }}>Delete</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Box variant="p">
        Delete &quot;{agentLabel}&quot;? This action cannot be undone.
      </Box>
    </Modal>
  )
}

// ─── CreateAgentModal ─────────────────────────────────────────────────────────

interface CreateAgentModalProps {
  agentType: CatalogItem | null
  onDismiss: () => void
  onSuccess: () => void
  onError: (msg: string) => void
}

function CreateAgentModal({ agentType, onDismiss, onSuccess, onError }: CreateAgentModalProps) {
  const { createClient, loading } = useCreateClient()
  const apolloClient = useApolloClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CreateClientFormData>({
    resolver: zodResolver(createClientSchema),
    defaultValues: { autoRun: false, autoPublish: false },
  })

  const handleCreate = handleSubmit(async (data) => {
    try {
      await createClient(data)
      await apolloClient.refetchQueries({ include: ['GetClientsNav'] })
      reset()
      onSuccess()
    } catch {
      onError('Failed to create agent. Please try again.')
    }
  })

  const handleDismiss = () => {
    reset()
    onDismiss()
  }

  return (
    <Modal
      visible={agentType !== null}
      onDismiss={handleDismiss}
      header={`Create ${agentType?.name ?? 'Agent'}`}
      data-testid="create-agent-modal"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>Cancel</Button>
            <Button variant="primary" loading={loading} onClick={() => { void handleCreate() }}>Create</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Brand Name" constraintText="Required" errorText={errors.brandName?.message}>
              <Controller name="brandName" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Agent Name" errorText={errors.agentName?.message}>
              <Controller name="agentName" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Location" errorText={errors.brandLocation?.message}>
              <Controller name="brandLocation" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Specialization" errorText={errors.brandSpecialization?.message}>
              <Controller name="brandSpecialization" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Industry" errorText={errors.industry?.message}>
              <Controller name="industry" control={control} render={({ field }) => (
                <Select
                  options={INDUSTRY_OPTIONS}
                  selectedOption={INDUSTRY_OPTIONS.find((o) => o.value === field.value) ?? null}
                  onChange={({ detail }) => field.onChange(detail.selectedOption.value)}
                  placeholder="Select industry"
                />
              )} />
            </FormField>
            <FormField label="Website URL" errorText={errors.websiteUrl?.message}>
              <Controller name="websiteUrl" control={control} render={({ field }) => (
                <Input value={field.value ?? ''} onChange={(e) => field.onChange(e.detail.value)} onBlur={field.onBlur} />
              )} />
            </FormField>
            <FormField label="Model Provider" errorText={errors.modelProvider?.message}>
              <Controller name="modelProvider" control={control} render={({ field }) => (
                <Select
                  options={MODEL_PROVIDER_OPTIONS}
                  selectedOption={MODEL_PROVIDER_OPTIONS.find((o) => o.value === field.value) ?? null}
                  onChange={({ detail }) => field.onChange(detail.selectedOption.value)}
                  placeholder="Select model provider"
                />
              )} />
            </FormField>
            <FormField label="Daily Budget (USD)" errorText={errors.budgetLimitDailyUsd?.message}>
              <Controller name="budgetLimitDailyUsd" control={control} render={({ field }) => (
                <Input
                  type="number"
                  value={field.value !== undefined ? String(field.value) : ''}
                  onChange={(e) => field.onChange(e.detail.value === '' ? undefined : Number(e.detail.value))}
                  onBlur={field.onBlur}
                />
              )} />
            </FormField>
          </ColumnLayout>
          <FormField label="Content Types" errorText={errors.selectedContentTypes?.message}>
            <Controller name="selectedContentTypes" control={control} render={({ field }) => (
              <Multiselect
                options={CONTENT_TYPE_OPTIONS}
                selectedOptions={(field.value ?? []).map((v) => ({ label: v.replace(/_/g, ' '), value: v }))}
                onChange={({ detail }) => field.onChange(detail.selectedOptions.map((o) => o.value as string))}
                placeholder="Select content types"
              />
            )} />
          </FormField>
          <Controller name="autoRun" control={control} render={({ field }) => (
            <Toggle checked={field.value ?? false} onChange={({ detail }) => field.onChange(detail.checked)}>
              Auto Run — automatically start campaigns
            </Toggle>
          )} />
          <Controller name="autoPublish" control={control} render={({ field }) => (
            <Toggle checked={field.value ?? false} onChange={({ detail }) => field.onChange(detail.checked)}>
              Auto Publish — automatically publish content
            </Toggle>
          )} />
        </SpaceBetween>
      </Form>
    </Modal>
  )
}

// ─── AgentsPage ───────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [
  { value: 6, label: '6 agents' },
  { value: 12, label: '12 agents' },
  { value: 24, label: '24 agents' },
]

export default function AgentsPage() {
  const { clients, loading } = useClientsNav()
  const [activeTabId, setActiveTabId] = useState('agents')
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null)
  const [deletingAgent, setDeletingAgent] = useState<ClientNavItem | null>(null)
  const [creatingType, setCreatingType] = useState<CatalogItem | null>(null)
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [pageSize, setPageSize] = useState(6)

  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    clients,
    {
      filtering: {
        empty: (
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s">
              <Box variant="p">No agents yet.</Box>
              <Button variant="primary" onClick={() => setActiveTabId('catalog')}>Browse catalog</Button>
            </SpaceBetween>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" padding="l">
            <Box variant="p">No agents match the filter.</Box>
          </Box>
        ),
        filteringFunction: (item, filterText) => {
          const label = (item.agentName ?? item.brandName ?? '').toLowerCase()
          return label.includes(filterText.toLowerCase())
        },
      },
      pagination: { pageSize },
    },
  )

  const clearFlash = () => setFlash([])

  const handleEditSuccess = () => {
    setEditingAgentId(null)
    setFlash([{ type: 'success', content: 'Agent updated successfully.', dismissible: true, onDismiss: clearFlash }])
  }

  const handleEditError = (msg: string) => {
    setFlash([{ type: 'error', content: msg, dismissible: true, onDismiss: clearFlash }])
  }

  const handleDeleteSuccess = () => {
    setDeletingAgent(null)
    setFlash([{ type: 'success', content: 'Agent deleted.', dismissible: true, onDismiss: clearFlash }])
  }

  const handleCreateSuccess = () => {
    setCreatingType(null)
    setActiveTabId('agents')
    setFlash([{ type: 'success', content: 'Agent created.', dismissible: true, onDismiss: clearFlash }])
  }

  const handleCardAction = (c: ClientNavItem, detail: ButtonDropdownProps.ItemClickDetails) => {
    if (detail.id === 'edit') setEditingAgentId(c.id)
    if (detail.id === 'delete') setDeletingAgent(c)
  }

  return (
    <Page
      title="Agents"
      helpContent={{
        header: 'Manage Agents',
        body: (
          <div>
            <p>Manage all your marketing agents across different clients. Create, edit, and manage agent configurations.</p>
            <h4>Available Actions:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li>View all agents and their status</li>
              <li>Create new agents for new clients</li>
              <li>Edit agent names, brands, and settings</li>
              <li>Select content types per agent</li>
              <li>Delete agents (careful - this is permanent)</li>
            </ul>
            <h4>Tips:</h4>
            <p>Each agent can be customized separately. Use descriptive agent names for easy identification.</p>
          </div>
        ),
      }}
    >
      <SpaceBetween size="m">
        <Flashbar items={flash} />
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              id: 'agents',
              label: 'My Agents',
              content: (
                <Cards
                  {...collectionProps}
                  items={items}
                  loading={loading}
                  loadingText="Loading agents…"
                  data-testid="agent-cards"
                  cardDefinition={{
                    header: (c) => c.agentName ?? c.brandName,
                    sections: [
                      {
                        id: 'brand',
                        header: 'Brand',
                        content: (c) => c.brandName,
                      },
                      {
                        id: 'actions',
                        content: (c) => (
                          <ButtonDropdown
                            variant="icon"
                            items={[
                              { id: 'edit', text: 'Edit' },
                              { id: 'delete', text: 'Delete' },
                            ]}
                            onItemClick={({ detail }) => handleCardAction(c, detail)}
                            ariaLabel="Agent actions"
                          />
                        ),
                      },
                    ],
                  }}
                  cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }, { minWidth: 900, cards: 3 }]}
                  header={
                    <Header
                      counter={filteredItemsCount !== undefined && filteredItemsCount < clients.length
                        ? `(${filteredItemsCount}/${clients.length})`
                        : `(${clients.length})`}
                      actions={
                        <Button variant="primary" onClick={() => setActiveTabId('catalog')}>
                          Create Agent
                        </Button>
                      }
                    >
                      Agents
                    </Header>
                  }
                  filter={
                    <TextFilter
                      {...filterProps}
                      filteringPlaceholder="Find agents"
                      filteringAriaLabel="Filter agents"
                    />
                  }
                  pagination={<Pagination {...paginationProps} />}
                  preferences={
                    <CollectionPreferences
                      title="Preferences"
                      confirmLabel="Confirm"
                      cancelLabel="Cancel"
                      preferences={{ pageSize }}
                      onConfirm={({ detail }) => setPageSize(detail.pageSize ?? 6)}
                      pageSizePreference={{ title: 'Page size', options: PAGE_SIZE_OPTIONS }}
                    />
                  }
                />
              ),
            },
            {
              id: 'catalog',
              label: 'Catalog',
              content: (
                <Cards
                  items={CATALOG_ITEMS as unknown as CatalogItem[]}
                  cardDefinition={{
                    header: (item) => item.name,
                    sections: [
                      {
                        id: 'description',
                        content: (item) => <Box variant="p">{item.description}</Box>,
                      },
                      {
                        id: 'action',
                        content: (item) => (
                          <Button variant="primary" onClick={() => setCreatingType(item)}>
                            Create
                          </Button>
                        ),
                      },
                    ],
                  }}
                  cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }, { minWidth: 900, cards: 3 }]}
                  header={<Header counter={`(${CATALOG_ITEMS.length})`}>Agent Catalog</Header>}
                  empty={
                    <Box textAlign="center" padding="l">
                      <Box variant="p">No agent types available.</Box>
                    </Box>
                  }
                />
              ),
            },
          ]}
        />
        {editingAgentId !== null && (
          <EditAgentModal
            agentId={editingAgentId}
            onDismiss={() => setEditingAgentId(null)}
            onSuccess={handleEditSuccess}
            onError={handleEditError}
          />
        )}
        <DeleteConfirmModal
          agent={deletingAgent}
          onDismiss={() => setDeletingAgent(null)}
          onSuccess={handleDeleteSuccess}
        />
        <CreateAgentModal
          agentType={creatingType}
          onDismiss={() => setCreatingType(null)}
          onSuccess={handleCreateSuccess}
          onError={(msg) => setFlash([{ type: 'error', content: msg, dismissible: true, onDismiss: clearFlash }])}
        />
      </SpaceBetween>
    </Page>
  )
}
